import { useMemo, useEffect, useRef } from 'react'
import { useEditorStore } from '../../stores/editorStore'
import { renderMarkdown, extractToc } from '../../utils/markdown'
import { debounce } from '../../utils/dom'
import type { TocItem } from '../../utils/markdown'

export default function MarkdownPreview() {
  const content = useEditorStore((s) => s.content)
  const previewRef = useRef<HTMLDivElement>(null)

  const html = useMemo(() => renderMarkdown(content), [content])
  const toc = useMemo(() => extractToc(content), [content])

  useEffect(() => {
    if (!previewRef.current) return
    const headings = previewRef.current.querySelectorAll('h1, h2, h3, h4, h5, h6')
    headings.forEach((heading) => {
      const text = heading.textContent || ''
      const id = text.toLowerCase().replace(/[^\w\u4e00-\u9fff]+/g, '-')
      heading.id = id
    })
  }, [html])

  useEffect(() => {
    const links = previewRef.current?.querySelectorAll('a')
    if (!links) return
    links.forEach((link) => {
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
    })
  }, [html])

  return (
    <div className="flex h-full">
      {toc.length > 0 && (
        <div className="hidden lg:block w-48 border-r border-editor-border p-3 overflow-auto text-sm shrink-0">
          <div className="font-semibold text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
            目录
          </div>
          {toc.map((item: TocItem, index: number) => (
            <a
              key={index}
              href={`#${item.id}`}
              className="block py-0.5 text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 truncate transition-colors"
              style={{ paddingLeft: `${(item.level - 1) * 12}px` }}
              onClick={(e) => {
                e.preventDefault()
                const el = document.getElementById(item.id)
                el?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              {item.text}
            </a>
          ))}
        </div>
      )}
      <div
        ref={previewRef}
        className="markdown-preview flex-1 overflow-auto p-6"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  )
}
